/**
 * Viewer360 - Main entry point for the 360° Viewer package
 * Provides a clean, programmatic API for initializing the viewer
 */
import { App } from './App.js';

export class Viewer360 {
    constructor(config = {}) {
        this.config = this.validateAndNormalizeConfig(config);
        this.app = null;
        this.isInitialized = false;
        this.userInfo = null; // Will be populated after JWT validation in edit mode
    }

    /**
     * Validates and normalizes the configuration
     * @param {Object} config - User-provided configuration
     * @returns {Object} Normalized configuration
     */
    validateAndNormalizeConfig(config) {
        const normalized = {
            mode: config.mode || 'edit',
            experienceId: config.experienceId || null, // Deprecated: use galleryId instead
            galleryId: config.galleryId || config.experienceId || null, // Preferred
            jwtToken: config.jwtToken || null,
            container: config.container || '#app',
            apiEndpoint: config.apiEndpoint || 'http://localhost:8000',
            projectId: config.projectId || null,
            autoAttach: config.autoAttach || false,

            // Lifecycle hooks
            onInit: config.onInit || (() => {}),
            onLoad: config.onLoad || (() => {}),
            onSave: config.onSave || (() => {}),
            onError: config.onError || ((err) => console.error('[Viewer360] Error:', err)),
            onImageChange: config.onImageChange || (() => {}),
            onDestroy: config.onDestroy || (() => {})
        };

        // Validate mode
        if (!['edit', 'view'].includes(normalized.mode)) {
            throw new Error(`[Viewer360] Invalid mode "${normalized.mode}". Must be "edit" or "view".`);
        }

        // Validate galleryId requirement for view mode
        if (normalized.mode === 'view' && !normalized.galleryId) {
            throw new Error('[Viewer360] galleryId (or experienceId) is required in "view" mode.');
        }

        // Validate authentication credentials based on mode
        if (normalized.mode === 'edit') {
            // Edit mode requires JWT token
            if (!normalized.jwtToken) {
                console.warn('[Viewer360] No JWT token provided. Edit mode requires authentication.');
            }
        } else if (normalized.mode === 'view') {
            // View mode is always public - no authentication required
            console.log('[Viewer360] View mode - Public access (no authentication required).');
        }

        // Validate container exists
        const containerElement = document.querySelector(normalized.container);
        if (!containerElement) {
            throw new Error(`[Viewer360] Container "${normalized.container}" not found in DOM.`);
        }

        return normalized;
    }

    /**
     * Initializes the viewer
     * @returns {Promise<void>}
     */
    async init() {
        if (this.isInitialized) {
            console.warn('[Viewer360] Viewer already initialized. Call destroy() first if you want to reinitialize.');
            return;
        }

        try {
            console.log('[Viewer360] Initializing viewer...', {
                mode: this.config.mode,
                galleryId: this.config.galleryId,
                hasJwtToken: !!this.config.jwtToken
            });

            // Validate JWT token in edit mode
            if (this.config.mode === 'edit' && this.config.jwtToken) {
                console.log('[Viewer360] Validating JWT token...');
                const apiService = new (await import('./services/ApiService.js')).ApiService(
                    this.config.jwtToken,
                    this.config.apiEndpoint,
                    this.config.mode
                );

                try {
                    const userInfo = await apiService.validateToken();
                    console.log('[Viewer360] ✅ JWT token valid. User:', userInfo.email);
                    // Store user info for potential future use
                    this.userInfo = userInfo;
                } catch (error) {
                    console.error('[Viewer360] ❌ JWT token validation failed:', error.message);
                    throw new Error(`Authentication failed: ${error.message}`);
                }
            }

            // Create App instance with jwtToken, endpoint, galleryId, projectId, and autoAttach
            this.app = new App(
                this.config.mode,
                this.config.jwtToken,
                this.config.apiEndpoint,
                this.config.galleryId,
                this.config.projectId,
                this.config.autoAttach
            );
            await this.app.init();

            // Store reference globally for debugging
            if (typeof window !== 'undefined') {
                window.viewer360 = this;
            }

            // If galleryId is provided, load the experience
            if (this.config.galleryId) {
                await this.loadExperience(this.config.galleryId);
            } else if (this.config.mode === 'edit') {
                // Edit mode without galleryId = creation mode
                console.log('[Viewer360] Edit mode - Creation. Waiting for initial image upload.');
                // Show the initial upload modal
                this.app.showInitialUploadModal();
            }

            this.isInitialized = true;
            this.config.onInit();

            console.log('[Viewer360] Initialization complete.');

        } catch (error) {
            console.error('[Viewer360] Initialization failed:', error);
            this.config.onError(error);
            throw error;
        }
    }

    /**
     * Loads an experience by gallery ID from the API
     * @param {string} galleryId - The gallery ID to load
     * @returns {Promise<void>}
     */
    async loadExperience(galleryId) {
        if (!this.app) {
            throw new Error('[Viewer360] App not initialized. Call init() first.');
        }

        try {
            console.log(`[Viewer360] Loading experience with gallery ID: ${galleryId}`);

            // Use ApiService to load experience
            const data = await this.app.apiService.loadExperience(galleryId);
            console.log('[Viewer360] Experience data loaded:', data);

            // Load into the App
            await this.app.loadApiData(data);

            this.config.onLoad(data);

            console.log('[Viewer360] Experience loaded successfully.');

        } catch (error) {
            console.error('[Viewer360] Failed to load experience:', error);
            this.config.onError(error);
            throw error;
        }
    }

    /**
     * Saves the current experience
     * @returns {Promise<Object>} Save result from API
     */
    async save() {
        if (!this.app) {
            throw new Error('[Viewer360] App not initialized. Call init() first.');
        }

        if (this.config.mode !== 'edit') {
            throw new Error('[Viewer360] Save is only available in edit mode.');
        }

        try {
            console.log('[Viewer360] Saving experience...');

            // Use the App's save functionality
            const result = await this.app.saveVirtualTour();

            this.config.onSave(result);

            console.log('[Viewer360] Experience saved successfully:', result);
            return result;

        } catch (error) {
            console.error('[Viewer360] Failed to save experience:', error);
            this.config.onError(error);
            throw error;
        }
    }

    /**
     * Destroys the viewer and cleans up resources
     */
    destroy() {
        if (!this.isInitialized) {
            console.warn('[Viewer360] Viewer not initialized. Nothing to destroy.');
            return;
        }

        try {
            console.log('[Viewer360] Destroying viewer...');

            if (this.app) {
                this.app.dispose();
                this.app = null;
            }

            this.isInitialized = false;
            this.config.onDestroy();

            // Remove global reference
            if (typeof window !== 'undefined' && window.viewer360 === this) {
                delete window.viewer360;
            }

            console.log('[Viewer360] Viewer destroyed.');

        } catch (error) {
            console.error('[Viewer360] Error during destruction:', error);
            this.config.onError(error);
        }
    }

    /**
     * Gets the current image index
     * @returns {number|null}
     */
    get currentImageIndex() {
        return this.app?.imageManager?.currentImageIndex ?? null;
    }

    /**
     * Gets all images
     * @returns {Array|null}
     */
    get images() {
        return this.app?.imageManager?.images ?? null;
    }

    /**
     * Gets the current image
     * @returns {Object|null}
     */
    get currentImage() {
        if (!this.app?.imageManager) return null;
        const { imageManager } = this.app;
        return imageManager.images[imageManager.currentImageIndex] ?? null;
    }

    /**
     * Checks if the viewer is ready
     * @returns {boolean}
     */
    get isReady() {
        return this.isInitialized && this.app?.isInitialized;
    }

    /**
     * Gets the current mode
     * @returns {string}
     */
    get mode() {
        return this.config.mode;
    }

    /**
     * Gets the experience ID (if loaded)
     * @returns {string|null}
     */
    get experienceId() {
        return this.config.experienceId;
    }

    /**
     * Gets the gallery ID (set after first save)
     * @returns {string|null}
     */
    get galleryId() {
        return this.app?.galleryId ?? null;
    }

    /**
     * Gets the scene ID / CDN path identifier (set after first save)
     * @returns {string|null}
     */
    get sceneId() {
        return this.app?.sceneId ?? null;
    }
}

// Export as default for easier imports
export default Viewer360;
